import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  applyCrmTheme,
  clearCrmTheme,
  readStoredThemeChoice,
  resolveCrmTheme,
  systemPrefersDark,
  writeStoredThemeChoice,
  type CrmTheme,
  type CrmThemeChoice,
} from './crmTheme';

type CrmThemeContextValue = {
  theme: CrmTheme;
  /** True when the user has picked light or dark, rather than following the system. */
  explicit: boolean;
  toggle: () => void;
};

const CrmThemeContext = createContext<CrmThemeContextValue | null>(null);

export const CrmThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [choice, setChoice] = useState<CrmThemeChoice>(() => readStoredThemeChoice());
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (choice) return undefined;
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [choice]);

  const theme = resolveCrmTheme(choice, systemDark);

  useEffect(() => {
    applyCrmTheme(theme);
    return () => clearCrmTheme();
  }, [theme]);

  const toggle = useCallback(() => {
    const next: CrmTheme = theme === 'dark' ? 'light' : 'dark';
    writeStoredThemeChoice(next);
    setChoice(next);
  }, [theme]);

  const value = useMemo(
    () => ({ theme, explicit: choice != null, toggle }),
    [theme, choice, toggle],
  );

  return <CrmThemeContext.Provider value={value}>{children}</CrmThemeContext.Provider>;
};

export function useCrmTheme(): CrmThemeContextValue {
  const ctx = useContext(CrmThemeContext);
  if (!ctx) {
    return {
      theme: 'light',
      explicit: false,
      toggle: () => {},
    };
  }
  return ctx;
}
